import { DateInput, EventInput } from '@fullcalendar/core';
import { IRolePersonel } from 'src/app/interfaces/Appointment/IAppointmentRoles';

// Define color options using RGBA values
const colors = {
  orange: 'rgba(255, 165, 0, 0.4)', // proposed, pending
  green: 'rgba(13, 148, 136, 0.4)', // booked
  blue: 'rgba(128, 186, 226, 0.4)', // arrived, checked-in
  purple: 'rgba(149, 128, 226, 0.4)', // fulfilled
  red: 'rgba(201, 105, 77, 0.4)', // cancelled, noshow
  yellow: 'rgba(216, 201, 111, 0.4)', // waitlist
  gray: 'rgba(160, 160, 160, 0.4)', // entered-in-error
  default: 'rgba(128, 186, 226, 0.4)', // fallback
};

// Function to generate event IDs
let eventGuid = 0;
export function createEventId() {
  return String(eventGuid++);
}

// Function to determine colors based on status
function getColorByStatusId(statusId: number): string {
  const colorMap: { [key: number]: string } = {
    1: colors.orange, // proposed
    2: colors.orange, // pending
    3: colors.green, // booked
    4: colors.blue, // arrived
    5: colors.purple, // fulfilled
    6: colors.red, // cancelled
    7: colors.red, // noshow
    8: colors.gray, // entered-in-error
    9: colors.blue, // checked-in
    10: colors.yellow, // waitlist
  };

  return colorMap[statusId] || colors.default;
}

function ensureISOString(input: DateInput, fallback: string): string {
  try {
    if (!input) return fallback;

    if (typeof input === 'string') {
      // If it's already a date-only string like "2025-04-06", append time
      if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
        return new Date(`${input}T00:00:00`).toISOString();
      }
      return new Date(input).toISOString();
    }

    if (input instanceof Date && !isNaN(input.getTime())) {
      return input.toISOString();
    }

    return fallback;
  } catch {
    return fallback;
  }
}

// Format date helper function
export function formatDate(date: any): string {
  if (!date) return '';
  const pad = (n: number) => (n < 10 ? '0' + n : n);

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1); // Months are zero-based
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());

  return `${year}-${month}-${day}T${hours}:${minutes}`; // Return in YYYY-MM-DDTHH:mm format
}

function getRoleNameForPersonnel(
  rolesList: IRolePersonel[],
  personnelId: number | string
): string {
  // Loop through each role in the roles list
  for (const role of rolesList) {
    // Check in main role users first
    if (role.users && role.users.some((user) => user.userId === personnelId)) {
      return role.roleName;
    }
    // Then, check among sub-role users if available
    if (
      role.subRoleUsers &&
      role.subRoleUsers.some((subRole) =>
        subRole.users.some((user) => user.userId === personnelId)
      )
    ) {
      // Find the sub-role so you can return its roleName
      const foundSubRole = role.subRoleUsers.find((subRole) =>
        subRole.users.some((user) => user.userId === personnelId)
      );
      if (foundSubRole) {
        return foundSubRole.roleName;
      }
    }
  }
  // Fallback: if not found in any roles, return a default role
  return 'No role';
}

// Function to map backend data to FullCalendar event format
export function mapBackendAppointmentDataToCalendarEvents(
  backendData: any
): EventInput[] {
  const allEvents: EventInput[] = [];

  backendData.forEach((appointment: any) => {
    const participants = appointment.participants || [];

    const personnel = participants
      .filter(
        (p: any) =>
          !p.actor.reference.startsWith('Patient') &&
          !p.actor.reference.startsWith('Department')
      )
      .map((p: any) => ({
        id: p.actor.userId,
        title: p.actor.display,
        participantStatusId: 30,
        participantId: p.id,
        actorId: p.actor.id,
        role: p.actor.reference.split('/')[0],
      }));

    const patients = participants
      .filter((p: any) => p.actor.reference.startsWith('Patient'))
      .map((p: any) => ({
        id: p.actor.patientId,
        title: p.actor.display,
        participantStatusId: 30,
        participantId: p.id,
        actorId: p.actor.id,
      }));

    const department = participants
      .filter((p: any) => p.actor.reference.startsWith('Department'))
      .map((p: any) => ({
        id: p.actor.reference.split('/')[1],
        title: p.actor.display,
        participantStatusId: 30,
        participantId: p.id,
        actorId: p.actor.id ?? null,
      }));

    const slots = appointment.slots || [];

    // For each slot, create a separate calendar event
    for (const slot of slots) {
      let isAllDay = false;

      if (slot?.start && slot?.end) {
        try {
          const startTime = new Date(slot.start).toTimeString().slice(0, 5);
          const endTime = new Date(slot.end).toTimeString().slice(0, 5);
          isAllDay = startTime === '00:00' && endTime === '23:59';
        } catch (e) {
          isAllDay = false;
        }
      }

      const event: EventInput = {
        id: appointment.id,
        title: appointment.description || 'Untitled Event',
        start: slot?.start || '',
        end: slot?.end || '',
        allDay: isAllDay,
        resourceIds: patients.map((p: any) => String(p.id)),
        extendedProps: {
          description: slot.comment || 'No description',
          personnel,
          patients,
          department,
          appointmentStatusId: appointment.appointmentStatusId, // current solution: default appointment status to "booked(id=3)" // appointment.appointmentStatusId,
          appointmentServiceTypeId: appointment.appointmentServiceTypeId,
          appointmentReasonId: appointment.appointmentReasonId,
          slotStatusId: slot.slotStatusId || 21, // current solution: default slot status to "free(id=21)" // slot?.slotStatusId,
          slotId: slot?.id,
          overbooked: slot?.overbooked || false,
          createdBy: appointment.createdBy,
          isCreator: appointment.isCreator,
          recurrentTypeId: appointment.recurrentTypeId || 1, // default to "does not repeat(id=1)" if not provided
          recurrenceOccurrences: appointment.recurrenceOccurrences || null,
        },
        color: getColorByStatusId(appointment.appointmentStatusId),
        // borderColor: getColorByStatusId(appointment.appointmentStatusId),
      };

      allEvents.push(event);
    }
  });
  return allEvents;
}

// Function to map FullCalendar event data back to backend appointment format
export function mapCalendarEventsToBackendAppointmentData(
  event: EventInput,
  rolesList: IRolePersonel[]
): any {
  // console.log('mapCalendarEventsToBackendAppointmentData', event);

  const isAllDay = event.allDay;

  // Format start date
  const startIso = ensureISOString(
    isAllDay
      ? new Date(new Date(event.start as string | number).setHours(0, 0, 0, 0))
      : formatDate(event.start),
    new Date().toISOString()
  );

  // Format end date
  const endIso = ensureISOString(
    isAllDay
      ? new Date(
          new Date(event.start as string | number).setHours(23, 59, 0, 0)
        )
      : formatDate(event.end),
    new Date().toISOString()
  );

  const fallbackSlot = {
    id: event.extendedProps?.['slotId'] || 0,
    start: startIso,
    end: endIso,
    comment: event.extendedProps?.['description'] || '',
    overbooked: event.extendedProps?.['overbooked'] || false,
    slotStatusId: event.extendedProps?.['slotStatusId'] || 21,
  };

  return {
    id: isNaN(Number(event.id)) ? 0 : Number(event.id),
    description: event.title,
    appointmentStatusId: event.extendedProps?.['appointmentStatusId'] || 3,
    appointmentServiceTypeId:
      event.extendedProps?.['appointmentServiceTypeId'] || null,
    appointmentReasonId: event.extendedProps?.['appointmentReasonId'] || null,
    recurrentTypeId: event.extendedProps?.['recurrentTypeId'] || 1, // fallback to default if not provided (default = does not repeat(id=1))
    recurrenceOccurrences:
      event.extendedProps?.['recurrenceOccurrences'] || null,
    slot: fallbackSlot,
    participants: [
      ...(event.extendedProps?.['personnel']?.map((personnel: any) => ({
        id: personnel.id !== undefined ? personnel.participantId : null,
        required: true,
        participantStatusId: event.extendedProps?.['personnelStatus'] || 30,
        actor: {
          id: personnel.id !== undefined ? personnel.actorId : null,
          reference: `${getRoleNameForPersonnel(rolesList, personnel.id)}/${
            personnel.id
          }`,
          display: personnel.title,
          userId: personnel.id,
        },
      })) || []),

      ...(event.extendedProps?.['patients']?.map((patient: any) => ({
        id: patient.id !== undefined ? patient.participantId : null,
        required: true,
        participantStatusId: event.extendedProps?.['patientStatus'] || 30,
        actor: {
          id: patient.id !== undefined ? patient.actorId : null,
          reference: `Patient/${patient.id}`,
          display: patient.title,
          patientId: patient.id,
        },
      })) || []),

      ...(Array.isArray(event.extendedProps?.['department'])
        ? event.extendedProps['department'].map((department: any) => ({
            id: department.id !== undefined ? department.participantId : null,
            required: true,
            participantStatusId:
              event.extendedProps?.['departmentStatus'] || 30,
            actor: {
              id: department.id !== undefined ? department.actorId : null,
              reference: `Department/${department.id}`,
              display: department.title,
            },
          }))
        : []),
    ],
  };
}
